import {
  Button,
  Divider,
  Grid,
  List,
  ListItem,
  Rating,
  Tooltip,
  Typography,
} from '@mui/material';
import { Dispatch, SetStateAction } from 'react';
import { KaziAvatar } from '../../../Shared Components/Kazi/KaziAvatar';
import { KaziModal } from '../../../Shared Components/Kazi/KaziModal';
import { getRatingFieldLabelByKey, Talent } from '../../../Types/Talent';
import {
  formatUserName,
  IsNullOrEmpty,
  pluralize,
} from '../../../Utils/String';
import {
  calculateAverageScore,
  calculateAverageScoreAndReviews,
} from '../../../Utils/TalentUtils';
import { ScoreList } from './ScoreList';

interface IViewTalentRatingModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  talentData: Talent;
}

export const ViewTalentRatingModal = ({
  open,
  setOpen,
  talentData,
}: IViewTalentRatingModalProps) => {
  /* ******************** Variables ******************** */
  const { averageScore } = calculateAverageScoreAndReviews(talentData);

  const maxCategory = Object.entries(talentData.ratings.averageScores).reduce(
    (max, [category, score]) => {
      return score > max.score ? { category, score } : max;
    },
    { category: '', score: -Infinity }, // Use string '' for category in the initial value to match types
  );
  /* ******************** Hooks ******************** */
  /* ******************** Functions ******************** */
  const handleClose = () => {
    setOpen(false);
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  if (!talentData) return null;
  return (
    <KaziModal
      title="Reviews"
      open={open}
      handleClose={handleClose}
      allowCloseOnOutsideClick={true}
      sx={{ width: 'fit-content', height: 'fit-content', overflowY: 'auto' }}
      content={
        <Grid
          sx={{
            width: {
              xs: '70vw',
              md: '50vw',
            },
            height: '60vh',
          }}
        >
          <Grid
            container
            sx={{ flexDirection: { xs: 'column', md: 'row' } }}
            gap={4}
          >
            <Grid md={4} display={'flex'} flexDirection={'column'} gap={4}>
              <Grid
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'center'}
                alignItems={'center'}
                gap={1}
              >
                <Grid item>
                  <Typography
                    variant="lora-title-large"
                    fontWeight={'bold'}
                    marginBottom={'16px'}
                  >
                    {averageScore}
                  </Typography>
                </Grid>
                <Grid item>
                  <Rating value={averageScore} readOnly precision={0.1} />
                </Grid>
                <Grid item>
                  <Typography
                    variant="lora-title-medium"
                    fontWeight={'bold'}
                    marginBottom={'16px'}
                  >
                    Best at {getRatingFieldLabelByKey(maxCategory.category)}
                  </Typography>
                </Grid>
              </Grid>
              <ScoreList scores={talentData.ratings.averageScores} />
            </Grid>
            <Grid
              md={7}
              display={'flex'}
              flexDirection={'column'}
              sx={{ height: '60vh', overflowY: { md: 'auto' } }}
            >
              {talentData.ratings.history.length > 0 ? (
                <>
                  <Typography
                    variant="lora-title-medium"
                    fontWeight={'bold'}
                    marginBottom={'16px'}
                  >
                    {pluralize(
                      talentData.ratings.history.length,
                      'rating',
                      'ratings',
                    )}
                  </Typography>
                  <List>
                    {talentData.ratings.history.map((item, index) => (
                      <>
                        {index > 0 && <Divider variant={'middle'} />}
                        <ListItem
                          key={item.client_locator + '_' + item.job_locator}
                        >
                          <Grid
                            display={'flex'}
                            flexDirection={'column'}
                            gap={1}
                          >
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              gap={2}
                            >
                              <KaziAvatar
                                locator={item?.client_locator}
                                first_name={item?.client_first_name}
                                last_name={item?.client_last_name}
                                width="24px"
                                sx={{ fontSize: 12 }}
                              />

                              <Typography
                                variant="lora-title-medium"
                                fontWeight={'bold'}
                              >
                                {formatUserName(
                                  item.client_first_name,
                                  item.client_last_name,
                                )}
                              </Typography>
                            </Grid>
                            <Grid
                              display={'flex'}
                              flexDirection={'row'}
                              gap={1}
                            >
                              <Tooltip
                                title={<ScoreList scores={item.scores} />}
                              >
                                <Button
                                  sx={{ all: 'unset', display: 'inline' }}
                                >
                                  <Rating
                                    size={'small'}
                                    value={calculateAverageScore(item.scores)}
                                    readOnly
                                    precision={0.1}
                                  />
                                </Button>
                              </Tooltip>
                              {/* <Typography variant="lora-body-small">
                                {item.job_title}
                              </Typography> */}
                            </Grid>
                            {!IsNullOrEmpty(item.feedback) && (
                              <Typography variant="lora-body-medium">
                                {item.feedback}
                              </Typography>
                            )}
                          </Grid>
                        </ListItem>
                      </>
                    ))}
                  </List>
                </>
              ) : (
                <Typography
                  variant="lora-body-large"
                  sx={{ textAlign: 'center', width: '100%' }}
                >
                  No ratings yet
                </Typography>
              )}
            </Grid>
          </Grid>
        </Grid>
      }
      actions={[]}
    />
  );
};
